import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  RefreshCw, 
  RotateCcw, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Trash2
} from 'lucide-react';
import { clearAllCachesAndRefresh, clearUsageValidationCache, forceRefreshPage } from '@/utils/cache-utils';
import { useToast } from '@/hooks/use-toast';

export const AppRefreshManagement = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState<'active' | 'inactive' | 'unsupported'>('unsupported');
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Check service worker status
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setServiceWorkerStatus('active');
      }).catch(() => {
        setServiceWorkerStatus('inactive');
      });
    }

    // Get last refresh timestamp
    const stored = localStorage.getItem('lastAppRefresh');
    if (stored) {
      setLastRefresh(new Date(stored).toLocaleString());
    }
  }, []);

  const handleHardRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Store refresh timestamp
      localStorage.setItem('lastAppRefresh', new Date().toISOString());
      
      toast({
        title: "Hard Refresh Starting",
        description: "Clearing all caches and refreshing the app...",
      });

      // Clear all caches and refresh
      setTimeout(() => {
        clearAllCachesAndRefresh();
      }, 1000);
      
    } catch (error) {
      console.error('Error during hard refresh:', error);
      toast({
        title: "Refresh Failed",
        description: "Failed to refresh the app. Please try manually.",
        variant: "destructive"
      });
      setIsRefreshing(false);
    }
  };

  const handleClearCacheOnly = async () => {
    setIsClearing(true);
    try {
      const success = clearUsageValidationCache();
      
      if (success) {
        toast({
          title: "Cache Cleared",
          description: "App cache has been cleared successfully.",
        });
      } else {
        toast({
          title: "Clear Failed",
          description: "Failed to clear cache completely.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast({
        title: "Clear Failed",
        description: "An error occurred while clearing cache.",
        variant: "destructive"
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleForceReload = () => {
    toast({
      title: "Force Reload",
      description: "Reloading the page...",
    });
    
    setTimeout(() => {
      forceRefreshPage();
    }, 500);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
          App Refresh Management
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage app cache and force refresh for PWA updates
        </p>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            App Status
          </CardTitle>
          <CardDescription>
            Current service worker and cache status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Service Worker</span>
            <Badge variant={serviceWorkerStatus === 'active' ? 'default' : 'secondary'}>
              {serviceWorkerStatus === 'active' ? 'Active' : 
               serviceWorkerStatus === 'inactive' ? 'Inactive' : 'Not Supported'}
            </Badge>
          </div>
          
          {lastRefresh && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Last Refresh</span>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {lastRefresh}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warning Alert */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Warning:</strong> Hard refresh will clear all cached data and reload the app. 
          Any unsaved changes will be lost. This is useful when you've published new updates to your PWA.
        </AlertDescription>
      </Alert>

      {/* Action Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Hard Refresh Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-500" />
              Hard Refresh
            </CardTitle>
            <CardDescription>
              Clear all caches and force reload the entire app. Use this after publishing updates.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleHardRefresh} 
              disabled={isRefreshing}
              className="w-full"
              size="lg"
            >
              {isRefreshing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Hard Refresh App
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Clear Cache Only Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-orange-500" />
              Clear Cache Only
            </CardTitle>
            <CardDescription>
              Clear app cache without reloading. Keeps current session active.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleClearCacheOnly} 
              disabled={isClearing}
              variant="outline"
              className="w-full"
              size="lg"
            >
              {isClearing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Cache
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Force Reload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-green-500" />
            Simple Page Reload
          </CardTitle>
          <CardDescription>
            Just reload the current page without clearing caches
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleForceReload} 
            variant="secondary"
            className="w-full"
            size="lg"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Force Page Reload
          </Button>
        </CardContent>
      </Card>

      {/* Usage Info */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg">Usage Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <strong>💡 When to use Hard Refresh:</strong>
            <p className="text-muted-foreground mt-1">
              After publishing new updates to your PWA, use this to ensure users get the latest version 
              without needing to delete and reinstall the app from their home screen.
            </p>
          </div>
          <div>
            <strong>🧹 When to use Clear Cache:</strong>
            <p className="text-muted-foreground mt-1">
              When experiencing data inconsistencies or want to clear stored cache without losing current session.
            </p>
          </div>
          <div>
            <strong>🔄 When to use Page Reload:</strong>
            <p className="text-muted-foreground mt-1">
              For simple refresh needs or when debugging UI issues that don't require cache clearing.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};